import type { HotelSearchExecution } from '@bandido/shared/types/hotels/hotel-search-progress';
import { Box, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

const errorKeys: Record<string, string> = {
  connector_configuration_error: 'configuration',
  connector_authentication_error: 'authentication',
  connector_decryption_error: 'credentials',
  connector_timeout: 'timeout',
  connector_transport_error: 'transport',
  connector_invalid_response: 'response',
  connector_normalization_error: 'normalization',
  connector_validation_error: 'validation',
  search_queue_expired: 'timeout',
};

/** Use normalized progress only, never provider payloads or free-text errors. */
export function HotelExecutionDetails({ executions }: { executions?: HotelSearchExecution[] }) {
  const { t } = useTranslation();
  if (!executions?.length) return null;
  return (
    <Box component="details">
      <Box component="summary" sx={{ cursor: 'pointer' }}>
        {t('hotelSearch.routesDetails')}
      </Box>
      <Stack gap={1} sx={{ mt: 1 }}>
        {executions.map((execution) => {
          const connector =
            execution.connector === 'hotelbeds'
              ? 'Hotelbeds - direct'
              : execution.connector === 'travelgate'
                ? 'Travelgate - tgx'
                : t('hotelSearch.routeConnector');
          const errorKey =
            execution.error_code && Object.prototype.hasOwnProperty.call(errorKeys, execution.error_code)
              ? errorKeys[execution.error_code]
              : 'unknown';
          return (
            <Box key={`${execution.connector_account_id}:${execution.access_code ?? ''}`}>
              <Typography variant="body2">
                {connector} · {t(`hotelSearch.routeStates.${execution.status}`)} ·{' '}
                {t('hotelSearch.routeOffers', { count: execution.fare_count })}
              </Typography>
              <Typography variant="caption" display="block">
                {t('hotelSearch.routeAccount', { account: execution.connector_account_id })}
                {execution.access_code && ` · ${t('hotelSearch.routeAccess', { access: execution.access_code })}`}
              </Typography>
              {execution.status === 'failed' && (
                <Typography variant="body2" color="warning.main">
                  {t(`hotelSearch.routeErrors.${errorKey}`)}
                  {errorKey !== 'unknown' && ` (${execution.error_code})`}
                </Typography>
              )}
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
}
